import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Typography, message, Alert, Row, Col, Spin } from 'antd';
import { ExclamationCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { api } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;

const ColumnMapping = ({ selectedTemplate, fileColumns, onMappingComplete }) => {
    const [mappings, setMappings] = useState({});
    const [validationErrors, setValidationErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savedMappingLoaded, setSavedMappingLoaded] = useState(false);

    useEffect(() => {
        if (selectedTemplate) {
            // Initialize mappings with empty values
            const initialMappings = {};
            Object.keys(selectedTemplate.fields).forEach(fieldKey => {
                initialMappings[fieldKey] = '';
            });
            setMappings(initialMappings);

            // Load saved mapping for this tenant and template
            loadSavedMapping();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- loadSavedMapping stable per selectedTemplate
    }, [selectedTemplate]);

    const loadSavedMapping = async () => {
        if (!selectedTemplate || !selectedTemplate.templateKey) return;

        setLoading(true);
        try {
            const response = await api.get(`/get-mapping/${selectedTemplate.templateKey}`);

            if (response.data.success && response.data.mappings) {
                // Convert array of mappings to object format
                const loadedMappings = {};
                response.data.mappings.forEach(mapping => {
                    // Only apply mapping if the file column exists in current file
                    if (fileColumns.includes(mapping.file_column)) {
                        loadedMappings[mapping.template_field] = mapping.file_column;
                    }
                });

                // Merge with existing mappings
                setMappings(prevMappings => ({
                    ...prevMappings,
                    ...loadedMappings
                }));

                setSavedMappingLoaded(true);

                // Count how many columns were auto-mapped
                const autoMappedCount = Object.keys(loadedMappings).length;
                if (autoMappedCount > 0) {
                    message.success(`${autoMappedCount} saved mappings applied automatically`);
                }

                // Validate the loaded mappings
                validateMappings({
                    ...mappings,
                    ...loadedMappings
                });
            }
        } catch (error) {
            // Silently fail if no saved mapping exists
            console.log('No saved mapping found or error loading:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMappingChange = (templateField, fileColumn) => {
        const newMappings = { ...mappings, [templateField]: fileColumn };
        setMappings(newMappings);
        validateMappings(newMappings);
    };

    const validateMappings = (currentMappings) => {
        const errors = [];

        if (!selectedTemplate) return;

        // Check required fields
        Object.entries(selectedTemplate.fields).forEach(([fieldKey, field]) => {
            if (field.required && !currentMappings[fieldKey]) {
                errors.push(`Required field "${field.name}" is not mapped`);
            }
        });

        // Check for duplicate mappings
        const usedColumns = Object.values(currentMappings).filter(col => col !== '');
        const uniqueColumns = [...new Set(usedColumns)];
        if (usedColumns.length !== uniqueColumns.length) {
            errors.push('Some file columns are mapped to multiple template fields');
        }

        setValidationErrors(errors);
    };

    const handleComplete = async () => {
        if (validationErrors.length > 0) {
            message.error('Please fix validation errors before proceeding');
            return;
        }

        // Convert mappings to the format expected by the API
        const mappingArray = Object.entries(mappings)
            .filter(([_, fileColumn]) => fileColumn !== '')
            .map(([templateField, fileColumn]) => ({
                template_field: templateField,
                file_column: fileColumn
            }));

        // Save mapping before proceeding
        setLoading(true);
        try {
            await api.post('/save-mapping', {
                template_name: selectedTemplate.templateKey,
                mappings: mappingArray
            });

            message.success('Mapping saved and applied successfully!');
            onMappingComplete(mappingArray);
        } catch (error) {
            console.error('Error saving mapping:', error);
            message.error('Error saving mapping');
        } finally {
            setLoading(false);
        }
    };

    const getFieldInfo = (fieldKey) => {
        if (!selectedTemplate) return null;
        return selectedTemplate.fields[fieldKey];
    };

    const isFieldRequired = (fieldKey) => {
        const field = getFieldInfo(fieldKey);
        return field ? field.required : false;
    };

    const getAvailableColumns = (currentField) => {
        // Get columns that are not already mapped to other fields
        const usedColumns = Object.entries(mappings)
            .filter(([fieldKey, _]) => fieldKey !== currentField)
            .map(([_, column]) => column)
            .filter(col => col !== '');

        return fileColumns.filter(col => !usedColumns.includes(col));
    };

    if (!selectedTemplate) {
        return (
            <Alert
                message="No template selected"
                description="Please go back and select a template first."
                type="warning"
                showIcon
            />
        );
    }

    return (
        <div>
            <Title level={4}>Map Columns</Title>
            <Text type="secondary">
                Map your file columns to the required template fields. Required fields must be mapped.
            </Text>

            {loading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>
                        <Text>Loading saved mappings...</Text>
                    </div>
                </div>
            )}

            {savedMappingLoaded && (
                <Alert
                    message="Saved mapping loaded"
                    description="Your previous mapping has been automatically applied. Please review and make changes if necessary."
                    type="info"
                    showIcon
                    closable
                    style={{ marginTop: 16, marginBottom: 16 }}
                />
            )}

            {validationErrors.length > 0 && (
                <Alert
                    message="Validation Errors"
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {validationErrors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    }
                    type="error"
                    icon={<ExclamationCircleOutlined />}
                    style={{ marginTop: 16, marginBottom: 16 }}
                />
            )}

            <div style={{ marginTop: 24 }}>
                <Row gutter={[16, 16]}>
                    {Object.entries(selectedTemplate.fields).map(([fieldKey, field]) => (
                        <Col xs={24} sm={12} lg={8} key={fieldKey}>
                            <Card size="small" style={{ height: '100%' }}>
                                <div className="mapping-field">
                                    <div className="field-label">
                                        {field.name}
                                        {field.required && (
                                            <span className="required-field" style={{ marginLeft: 8 }}>*</span>
                                        )}
                                    </div>

                                    <div className="field-description">
                                        {field.description}
                                    </div>

                                    <div style={{ marginTop: 8 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Type: <Text code>{field.type}</Text>
                                        </Text>
                                    </div>

                                    <Select
                                        style={{ width: '100%', marginTop: 8 }}
                                        placeholder="Select file column"
                                        value={mappings[fieldKey] || undefined}
                                        onChange={(value) => handleMappingChange(fieldKey, value)}
                                        allowClear
                                    >
                                        {getAvailableColumns(fieldKey).map(column => (
                                            <Option key={column} value={column}>
                                                {column}
                                            </Option>
                                        ))}
                                    </Select>

                                    {isFieldRequired(fieldKey) && !mappings[fieldKey] && (
                                        <Text type="danger" style={{ fontSize: 12 }}>
                                            This field is required
                                        </Text>
                                    )}
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>

            <div style={{ marginTop: 24, textAlign: 'center' }}>
                <Button
                    type="primary"
                    size="large"
                    icon={<SaveOutlined />}
                    onClick={handleComplete}
                    disabled={validationErrors.length > 0}
                    loading={loading}
                >
                    Save & Complete Mapping
                </Button>
            </div>

            <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Text type="secondary">
                    Mapped fields: {Object.values(mappings).filter(col => col !== '').length} / {Object.keys(selectedTemplate.fields).length}
                </Text>
            </div>
        </div>
    );
};

export default ColumnMapping;




